// src/components/OwnerAccount.vue
<template>
  <v-container>
    <h1>Owner Account</h1>
    <div class="button-group">
      <v-btn
        class="add-button"
        @click="openAddForm"
      >
        Add Company
      </v-btn>
      <v-btn
        class="audio-button"
        @click="toggleAudio(`${baseUrl}/audiofiles/alabamaexample.mp3`)"
        :color="isPlaying && currentAudio === `${baseUrl}/audiofiles/alabamaexample.mp3` ? 'error' : 'primary'"
      >
        {{ isPlaying && currentAudio === `${baseUrl}/audiofiles/alabamaexample.mp3` ? 'Stop Audio' : 'Alabama Example Audio' }}
      </v-btn>
      <v-btn
        class="audio-button"
        @click="toggleAudio(`${baseUrl}/audiofiles/connecticutexample.mp3`)"
        :color="isPlaying && currentAudio === `${baseUrl}/audiofiles/connecticutexample.mp3` ? 'error' : 'primary'"
      >
        {{ isPlaying && currentAudio === `${baseUrl}/audiofiles/connecticutexample.mp3` ? 'Stop Audio' : 'Connecticut Example Audio' }}
      </v-btn>
    </div>

    <!-- Company list -->
    <div class="company-list">
      <div
        v-for="(company, index) in companies"
        :key="company._id"
        class="company-row"
        :class="{ 'selected': selectedCompanyIndex === index }"
        :style="{ backgroundColor: selectedCompanyIndex === index ? '#d3d3d3' : index % 2 === 0 ? '#f9f9f9' : '#ffffff' }"
        @click="selectCompany(company, index)"
      >
        <!-- Company icon -->
        <img
          :src="company.icon || ''"
          class="company-icon"
          alt="Company Icon"
        />

        <!-- Company details -->
        <div class="company-details">
          <span class="company-number">#{{ company.companyNumber }}</span>
          <span class="company-name">{{ company.name }}</span>
        </div>

        <!-- SMS Enabled indicator -->
        <div class="sms-enabled">
          <template v-if="company.smsPhoneNumber">
            <v-progress-circular
              v-if="togglingSmsIds.includes(company._id)"
              indeterminate
              size="20"
              width="2"
              color="grey"
            ></v-progress-circular>
            <v-tooltip v-else location="top">
              <template v-slot:activator="{ props }">
                <v-icon
                  v-bind="props"
                  :color="smsEnvironmentColor(company)"
                  :style="{ cursor: smsEnvironmentOf(company) === 'unknown' ? 'help' : 'pointer' }"
                  @click.stop="toggleSmsWebhook(company)"
                >mdi-check-circle</v-icon>
              </template>
              <span>{{ smsEnvironmentTooltip(company) }}</span>
            </v-tooltip>
          </template>
        </div>

        <!-- Add SMS / Remove SMS button (swaps based on smsPhoneNumber) -->
        <v-btn
          v-if="!company.smsPhoneNumber"
          class="add-sms-button"
          @click.stop="addSms(company)"
        >
          Add SMS
        </v-btn>
        <v-btn
          v-else
          class="remove-sms-button"
          @click.stop="confirmRemoveSms(company)"
        >
          Remove SMS
        </v-btn>

        <!-- Edit button -->
        <v-btn class="edit-button" @click.stop="editCompany(company)">Edit</v-btn>

        <!-- Archive button -->
        <v-btn class="archive-button" @click.stop="confirmArchive(company)">Archive</v-btn>
      </div>
    </div>

    <!-- Leads component -->
    <Leads />
    <div class="bottom-spacer"></div>

    <!-- Edit Company dialog -->
    <OwnerAccountEditCompany
      v-model="showEditDialog"
      :company="companyToEdit || {}"
      @saved="onCompanySaved"
    />

    <!-- Add Company dialog -->
    <OwnerAccountAddCompany
      v-model="showAddDialog"
      @saved="onCompanyAdded"
    />

    <!-- Add SMS dialog -->
    <OwnerAccountAddSMS
      v-model="showAddSmsDialog"
      :company="smsCompany || {}"
      @saved="onSmsAdded"
    />

    <!-- Remove SMS Confirmation Dialog -->
    <v-dialog v-model="showRemoveSmsDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="archive-dialog-title">Remove SMS Number</v-card-title>
        <v-card-text>
          <p>
            <strong>Warning:</strong> The following Twilio SMS number will be permanently
            de-provisioned. The number will be released back to Twilio's pool and cannot be
            recovered.
          </p>
          <ul class="phone-list" v-if="removeSmsCandidate">
            <li>
              <strong>SMS:</strong> {{ removeSmsCandidate.smsPhoneNumber }}
            </li>
          </ul>
          <p class="mt-3">
            Are you sure you want to remove SMS for
            <strong>{{ removeSmsCandidate?.name }}</strong>?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelRemoveSms" :disabled="removingSms">Cancel</v-btn>
          <v-btn text color="error" @click="removeSms" :disabled="removingSms">
            {{ removingSms ? 'Removing...' : 'Yes, Remove' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Archive Confirmation Dialog -->
    <v-dialog v-model="showArchiveDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="archive-dialog-title">Archive Company</v-card-title>
        <v-card-text>
          <p>
            <strong>Warning:</strong> The following Twilio numbers will be permanently
            de-provisioned and this company will no longer function. This cannot be undone.
          </p>
          <ul class="phone-list" v-if="archiveCandidate">
            <li v-if="archiveCandidate.bbasMainPhone">
              <strong>Main:</strong> {{ archiveCandidate.bbasMainPhone }}
            </li>
            <li v-if="archiveCandidate.voicemailPhone">
              <strong>Voicemail:</strong> {{ archiveCandidate.voicemailPhone }}
            </li>
            <li v-if="archiveCandidate.menuDirectPhone">
              <strong>Menu:</strong> {{ archiveCandidate.menuDirectPhone }}
            </li>
          </ul>
          <p class="mt-3">
            All users for <strong>{{ archiveCandidate?.name }}</strong> will also be set to inactive.
          </p>
          <p class="mt-2">Are you sure you want to archive this company?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelArchive" :disabled="archiving">Cancel</v-btn>
          <v-btn text color="error" @click="archiveCompany" :disabled="archiving">
            {{ archiving ? 'Archiving...' : 'Yes, Archive' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Generalized Error Dialog -->
    <v-dialog v-model="errorDialog.show" max-width="400px">
      <v-card>
        <v-card-title>{{ errorDialog.title }}</v-card-title>
        <v-card-text>{{ errorDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="errorDialog.show = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import axios from 'axios';
import { mapState, mapActions } from 'vuex';
import Leads from './Leads.vue';
import OwnerAccountEditCompany from './OwnerAccount-EditCompany.vue';
import OwnerAccountAddCompany from './OwnerAccount-AddCompany.vue';
import OwnerAccountAddSMS from './OwnerAccount-AddSMS.vue';

export default {
  name: 'OwnerAccount',

  components: {
    Leads,
    OwnerAccountEditCompany,
    OwnerAccountAddCompany,
    OwnerAccountAddSMS
  },

  created() {
    console.log('OwnerAccount component has been rendered.');
  },

  data() {
    return {
      baseUrl: process.env.VUE_APP_THIS_SERVER,
      companies: [],
      showEditDialog: false,
      showAddDialog: false,
      companyToEdit: null,
      selectedCompanyIndex: null,
      audio: null,
      isPlaying: false,
      currentAudio: null,
      showArchiveDialog: false,
      archiveCandidate: null,
      archiving: false,
      showAddSmsDialog: false,
      smsCompany: null,
      showRemoveSmsDialog: false,
      removeSmsCandidate: null,
      removingSms: false,
      smsEnvironments: {},   // { [companyId]: 'test' | 'production' | 'unknown' }
      togglingSmsIds: [],    // companies whose webhook toggle is in-flight
      errorDialog: {
        show: false,
        title: '',
        message: ''
      },
    };
  },

  computed: {
    ...mapState(['selectedCompany']),
  },

  methods: {
    ...mapActions(['setSelectedCompany']),

    toggleAudio(audioUrl) {
      if (this.audio && this.currentAudio === audioUrl) {
        this.audio.pause();
        this.audio.currentTime = 0;
        this.audio = null;
        this.isPlaying = false;
        this.currentAudio = null;
      } else {
        if (this.audio) {
          this.audio.pause();
        }
        this.audio = new Audio(audioUrl);
        this.currentAudio = audioUrl;
        this.audio.addEventListener('ended', () => {
          this.isPlaying = false;
          this.currentAudio = null;
        });
        this.audio.play();
        this.isPlaying = true;
      }
    },

    async fetchCompanies() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_THIS_SERVER}/portaldb/db/companies`);
        this.companies = response.data
          .filter(c => c.status !== 'inactive')
          .map(company => ({ ...company }));
        // Once companies are loaded, fetch the SMS webhook environments in bulk
        this.fetchSmsEnvironments();
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    },

    async fetchSmsEnvironments() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_THIS_SERVER}/portaldb/db/sms/webhook-environments`
        );
        const map = {};
        for (const entry of response.data.environments || []) {
          map[entry.companyId] = entry.environment;
        }
        this.smsEnvironments = map;
      } catch (error) {
        console.error('Error fetching SMS webhook environments:', error);
        // Leave smsEnvironments empty; checkmarks will fall back to 'unknown' (red)
      }
    },

    smsEnvironmentOf(company) {
      return this.smsEnvironments[company._id] || 'unknown';
    },

    smsEnvironmentColor(company) {
      switch (this.smsEnvironmentOf(company)) {
        case 'production': return 'success';   // green
        case 'test':       return 'warning';   // yellow
        default:           return 'error';     // red
      }
    },

    smsEnvironmentTooltip(company) {
      const env = this.smsEnvironmentOf(company);
      if (env === 'production') return 'Messaging webhook currently configured for Production';
      if (env === 'test')       return 'Messaging webhook currently configured for Test';
      return 'Messaging webhook is not configured for a known environment';
    },

    async toggleSmsWebhook(company) {
      // No-op for unknown — the user must fix the webhook manually in Twilio
      if (this.smsEnvironmentOf(company) === 'unknown') return;
      if (this.togglingSmsIds.includes(company._id)) return;

      this.togglingSmsIds.push(company._id);
      try {
        const response = await axios.patch(
          `${process.env.VUE_APP_THIS_SERVER}/portaldb/db/companies/${company._id}/sms/webhook`
        );
        // Replace the value reactively
        this.smsEnvironments = {
          ...this.smsEnvironments,
          [company._id]: response.data.environment
        };
      } catch (error) {
        console.error('Error toggling SMS webhook:', error);
        const message = error.response?.data?.message || 'Failed to toggle SMS webhook environment.';
        this.showError('Toggle SMS Webhook Failed', message);
      } finally {
        this.togglingSmsIds = this.togglingSmsIds.filter(id => id !== company._id);
      }
    },

    openAddForm() {
      this.showAddDialog = true;
    },

    editCompany(company) {
      this.companyToEdit = company;
      this.showEditDialog = true;
    },

    addSms(company) {
      this.smsCompany = company;
      this.showAddSmsDialog = true;
    },

    onSmsAdded(updatedCompany) {
      const index = this.companies.findIndex(c => c._id === updatedCompany._id);
      if (index !== -1) {
        this.companies.splice(index, 1, { ...this.companies[index], ...updatedCompany });
      }
      // Newly provisioned numbers point at THIS_SERVER's webhook, which is
      // production in prod and test in test — match what the backend just set.
      this.smsEnvironments = {
        ...this.smsEnvironments,
        [updatedCompany._id]: this.detectCurrentServerEnvironment()
      };
      this.smsCompany = null;
    },

    // Heuristic: if the frontend is talking to bbas-test.me, we're in test;
    // if bbas.me, we're in production. Anything else is 'unknown'.
    detectCurrentServerEnvironment() {
      try {
        const host = new URL(process.env.VUE_APP_THIS_SERVER).hostname;
        if (host === 'bbas-test.me') return 'test';
        if (host === 'bbas.me') return 'production';
      } catch (e) { /* fall through */ }
      return 'unknown';
    },

    confirmRemoveSms(company) {
      this.removeSmsCandidate = company;
      this.showRemoveSmsDialog = true;
    },

    cancelRemoveSms() {
      this.showRemoveSmsDialog = false;
      this.removeSmsCandidate = null;
    },

    async removeSms() {
      if (!this.removeSmsCandidate) return;
      this.removingSms = true;

      try {
        const response = await axios.delete(
          `${process.env.VUE_APP_THIS_SERVER}/portaldb/db/companies/${this.removeSmsCandidate._id}/sms`
        );

        const updatedCompany = response.data.company;
        const index = this.companies.findIndex(c => c._id === updatedCompany._id);
        if (index !== -1) {
          this.companies.splice(index, 1, { ...this.companies[index], ...updatedCompany });
          // Vue may not pick up an unset field via spread; force the deletion locally too
          delete this.companies[index].smsPhoneNumber;
        }
        // Drop the stale env entry
        // eslint-disable-next-line no-unused-vars
        const { [updatedCompany._id]: _removed, ...rest } = this.smsEnvironments;
        this.smsEnvironments = rest;

        this.showRemoveSmsDialog = false;
        this.removeSmsCandidate = null;
      } catch (error) {
        console.error('Error removing SMS number:', error);
        const message = error.response?.data?.message || 'Failed to remove SMS number. Please try again.';
        this.showError('Remove SMS Failed', message);
      } finally {
        this.removingSms = false;
      }
    },

    showError(title, message) {
      this.errorDialog = { show: true, title, message };
    },

    confirmArchive(company) {
      this.archiveCandidate = company;
      this.showArchiveDialog = true;
    },

    cancelArchive() {
      this.showArchiveDialog = false;
      this.archiveCandidate = null;
    },

    async archiveCompany() {
      if (!this.archiveCandidate) return;
      this.archiving = true;

      try {
        await axios.patch(
          `${process.env.VUE_APP_THIS_SERVER}/portaldb/db/companies/${this.archiveCandidate._id}/archive`
        );

        // Remove from local list
        this.companies = this.companies.filter(c => c._id !== this.archiveCandidate._id);

        // If this was the selected company, clear it
        if (this.selectedCompany?._id === this.archiveCandidate._id) {
          this.setSelectedCompany(null);
          this.selectedCompanyIndex = null;
        }

        this.showArchiveDialog = false;
        this.archiveCandidate = null;
      } catch (error) {
        console.error('Error archiving company:', error);
        this.showError('Archive Failed', 'Failed to archive company. Please try again.');
      } finally {
        this.archiving = false;
      }
    },

    onCompanySaved(updatedCompany) {
      const index = this.companies.findIndex(c => c._id === updatedCompany._id);
      if (index !== -1) {
        this.companies.splice(index, 1, { ...this.companies[index], ...updatedCompany });
      }
    },

    onCompanyAdded(newCompany) {
      this.companies.unshift(newCompany);
    },

    selectCompany(company, index) {
      this.selectedCompanyIndex = index;
      this.setSelectedCompany(company);
    },
  },

  mounted() {
    this.fetchCompanies();
  },

  beforeUnmount() {
    if (this.audio) {
      this.audio.pause();
      this.audio.removeEventListener('ended', () => {});
      this.audio = null;
    }
  },
};
</script>

<style scoped>
.bottom-spacer {
  height: 300px;
  width: 100%;
}

.button-group {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.company-list {
  margin-top: 20px;
  max-height: 340px;
  overflow-y: auto;
}

.company-row {
  display: flex;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.company-row.selected {
  background-color: #d3d3d3 !important;
}

.company-icon {
  width: 50px;
  height: 50px;
  margin-right: 20px;
}

.company-details {
  display: flex;
  align-items: center;
  gap: 20px;
}

.company-number {
  width: 80px;
  text-align: left;
}

.company-name {
  width: 250px;
  font-weight: bold;
}

.edit-button {
  margin-left: 20px;
  background-color: #d2b48c;
}

.sms-enabled {
  width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 20px;
}

.add-sms-button {
  margin-left: 10px;
  width: 130px;
  background-color: #d2b48c;
}

.remove-sms-button {
  margin-left: 10px;
  width: 130px;
  background-color: #c0392b;
  color: #ffffff;
}

.archive-button {
  margin-left: 10px;
  background-color: #c0392b;
  color: #ffffff;
}

.add-button, .audio-button {
  background-color: #d2b48c;
}

.archive-dialog-title {
  color: #c0392b;
  font-weight: bold;
}

.phone-list {
  margin: 10px 0 0 20px;
  line-height: 2;
}

@media (max-width: 600px) {
  :deep(.v-container) {
    padding: 0 !important;
    margin: 0 !important;
    max-width: 100% !important;
  }

  .button-group {
    flex-direction: column;
    width: 100%;
  }

  .button-group .v-btn {
    width: 100%;
  }
}
</style>
